import { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  IconButton,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  AddCircleOutline,
  Public,
} from "@mui/icons-material";
import IOSSwitch from "../../../components/IOSSwitch";
import axiosInstance from "../../../axios/axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { END_POINTS } from "../../../shared";
import { useEncryptService } from "../../../shared/hooks";
import { toast } from "react-toastify";
import { data } from "react-router-dom";

const MapCredentialPopup = ({
  open,
  onClose,
  assetsCredData,
  credData,
  deviceId,
  mapCallback,
}: any) => {
  const [isCreatingNewCredential, setIsCreatingNewCredential] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showExecEyePassword, setShowExecEyePassword] = useState(false);

  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);
  const handleToggleExecPasswordVisibility = () =>
    setShowExecEyePassword(!showExecEyePassword);
  const { doEncryptLogin } = useEncryptService();
  const [ppkFile, setPpkFile] = useState<File | null>(null);
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const ppkFileInputRef = useRef<HTMLInputElement>(null);

  const resetTouchedFields = () => {
    formik.setTouched({
      selectedCredential: false,
      credentialType: false,
      passwordType: false,
      user: false,
      password: false,
      execPassword: false,
      apiToken: false,
      credentialName: false,
      saveCredentials: false,
      autoAssign: false,
      showExecPassword: false,
      execPasswordGroup: false,
      selectedCredenntialId: false,
    }, false);
  };

  const handleCredentialChange = async (event: any) => {
    const value = event.target.value;

    const isCreateNew = value === "create_new";
    if (isCreateNew) {
      setIsCreatingNewCredential(true);
      setShowExecEyePassword(false);
    } else {
      setIsCreatingNewCredential(false);
    }

    resetTouchedFields();
    await formik.setValues({
      ...formik.values,
      selectedCredential: value,
      credentialType: "ssh",
      passwordType: isCreateNew ? "password" : "none",
      selectedCredenntialId: value === "create_new" ? "create_new" :
        assetsCredData.find((cred: any) => cred.name === value)?.id || "",
      user: isCreateNew ? formik.values.user : "",
      password: isCreateNew ? formik.values.password : "",
      execPassword: isCreateNew ? formik.values.execPassword : "",
      apiToken: isCreateNew ? formik.values.apiToken : "",
      credentialName: isCreateNew ? formik.values.credentialName : "",
      saveCredentials: isCreateNew ? formik.values.saveCredentials : false,
      autoAssign: isCreateNew ? formik.values.autoAssign : false,
      showExecPassword: isCreateNew ? formik.values.showExecPassword : false,
      execPasswordGroup: isCreateNew ? formik.values.execPasswordGroup : "",
    });

    setIsCreatingNewCredential(value === "create_new");
    await formik.validateForm();
  };

  const formik = useFormik({
    initialValues: {
      selectedCredential: "",
      credentialType: "",
      passwordType: "",
      user: "",
      password: "",
      execPassword: "",
      apiToken: "",
      credentialName: "",
      saveCredentials: false,
      autoAssign: false,
      showExecPassword: false,
      execPasswordGroup: "",
      selectedCredenntialId: "",
    },
    validationSchema: Yup.object({

      selectedCredential: Yup.string()
        .required("Credential is required")
        .notOneOf([""], "Credential is required"),

      credentialName: Yup.string().when("selectedCredential", {
        is: "create_new",
        then: (schema) => schema.required("Credential Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),


      saveCredentials: Yup.boolean().notRequired(),


      apiToken: Yup.string().when(["selectedCredential", "credentialType"], {
        is: (selectedCredential: any, credentialType: any) =>
          selectedCredential === "create_new" && credentialType === "api",
        then: (schema) => schema.required("API Token is required"),
        otherwise: (schema) => schema.notRequired(),
      }),


      passwordType: Yup.string().when(["selectedCredential", "credentialType"], {
        is: (selectedCredential: any, credentialType: any) =>
          selectedCredential === "create_new" && credentialType === "ssh",
        then: (schema) =>
          schema
            .required("Password Type is required")
            .notOneOf(["none", ""], "Password Type is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      user: Yup.string().when(["selectedCredential", "credentialType"], {
        is: (selectedCredential: any, credentialType: any) =>
          selectedCredential === "create_new" && credentialType === "ssh",
        then: (schema) => schema.required("User is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

      password: Yup.string().when(
        ["selectedCredential", "credentialType", "passwordType"],
        {
          is: (selectedCredential: any, credentialType: any, passwordType: any) =>
            selectedCredential === "create_new" &&
            credentialType === "ssh" &&
            passwordType === "password",
          then: (schema) => schema.required("Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      execPasswordGroup: Yup.mixed().when(
        ["selectedCredential", "credentialType", "passwordType"],
        {
          is: (selectedCredential: any, credentialType: any, passwordType: any) =>
            selectedCredential === "create_new" &&
            credentialType === "ssh" &&
            passwordType === "password",
          then: (schema) =>
            schema.test(
              "at-least-one-toggle",
              "Either Auto Assign or Exec Password must be enabled",
              function () {
                const { autoAssign, showExecPassword } = this.parent;
                return autoAssign || showExecPassword;
              }
            ),
          otherwise: (schema) => schema.notRequired(),
        }
      ),

      execPassword: Yup.string().when(
        [
          "selectedCredential",
          "credentialType",
          "passwordType",
          "showExecPassword",
        ],
        {
          is: (
            selectedCredential: any,
            credentialType: any,
            passwordType: any,
            showExecPassword: any
          ) =>
            selectedCredential === "create_new" &&
            credentialType === "ssh" &&
            passwordType === "password" &&
            showExecPassword,
          then: (schema) => schema.required("Exec Password is required"),
          otherwise: (schema) => schema.notRequired(),
        }
      ),
    }),
    onSubmit: async (values: any) => {
      try {

        if (values.selectedCredential !== "create_new" && deviceId == null) {
          mapCallback(values);
          return;
        } else {
          const payload: any = {

            new_cred: values.selectedCredential === "create_new" ? true : false,
          };

          if (deviceId != null) {
            payload.asset_id = deviceId;
          }

          if (values.selectedCredential === "create_new") {
            payload.loginParameter = values.passwordType;
            payload.cred_name = values.credentialName;
            payload.save = values.saveCredentials;
            payload.global_cred = false;
            payload.description = "";
            payload.encrypt = true;
            payload.role_id = [];
            payload.user_id = [];
            if (values.credentialType === "api") {
              const APIType = credData[1].password_type.find(
                (type: { code: string }) => type.code === "api_token"
              );
              if (APIType) {
                payload.credential_type_id = APIType.id;
              }
              payload.custom_port = false;
              payload.api_token = doEncryptLogin(values.apiToken);
            } else {
              payload.username = doEncryptLogin(values.user);
              payload.auto_assign = values.autoAssign;
              if (values.showExecPassword) {
                payload.exec_password = values.showExecPassword;
                payload.enable_password = doEncryptLogin(values.execPassword);
              }
              if (values.passwordType === "password") {
                const passwordType = credData[0].password_type.find(
                  (type: { code: string }) => type.code === "password"
                );
                if (passwordType) {
                  payload.credential_type_id = passwordType.id;
                }
                payload.password = doEncryptLogin(values.password);
              }
              if (values.passwordType === "private_key") {
                const privateKeyType = credData[0].password_type.find(
                  (type: { code: string }) => type.code === "private_key"
                );
                if (privateKeyType) {
                  payload.credential_type_id = privateKeyType.id;
                }
                // payload.private_key will be handled via multipart upload when submitting
              }
            }
          } else {
            payload.user_credentials_id = values.selectedCredenntialId;
          }



          // If creating new SSH credential with private_key file, submit multipart form first
          if (values.selectedCredential === "create_new" && values.passwordType === "private_key") {
            const keyFile = ppkFile;
            if (!keyFile) {
              toast.error("Please upload a PEM file (.pem)");
              return;
            }

            const privateKeyType = credData[0].password_type.find(
              (type: { code: string }) => type.code === "private_key"
            );
            if (privateKeyType) {
              payload.credential_type_id = privateKeyType.id;
            }

            // include private_key_name to match Rescan flow
            payload.private_key_name = privateKeyFileName;

            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
              if (value === undefined || value === null) {
                return;
              }

              if (Array.isArray(value)) {
                formData.append(key, JSON.stringify(value));
                return;
              }

              if (typeof value === "boolean") {
                formData.append(key, value ? "true" : "false");
                return;
              }

              formData.append(key, value as any);
            });

            formData.append("private_key", keyFile);

            try {
              // Create credential first
              const createResp = await axiosInstance.post(END_POINTS.CREDENTIAL_MGMT, formData, {
                headers: { "Content-Type": "multipart/form-data" },
              });

              if (createResp?.status === 200) {
                // If deviceId present, map created credential to asset
                if (deviceId != null) {
                  const createdId =
                    createResp.data?.data?.id ||
                    createResp.data?.data?.credential_id ||
                    createResp.data?.data?.user_credentials_id ||
                    createResp.data?.data?.credential?.id;

                  if (!createdId) {
                    // Fallback: return create response to callback
                    mapCallback(createResp.data);
                    formik.resetForm();
                    setIsCreatingNewCredential(false);
                    onClose();
                    return;
                  }

                  const mapPayload: any = {
                    asset_id: deviceId,
                    user_credentials_id: createdId,
                  };

                  const mapResp = await axiosInstance.post(END_POINTS.MAP_CRED, mapPayload);
                  if (mapResp.status === 200) {
                    formik.resetForm();
                    setIsCreatingNewCredential(false);
                    mapCallback(mapResp.data);
                    onClose();
                  } else {
                    console.error("Map credential failed:", mapResp.data);
                  }
                } else {
                  // No deviceId: just created credential
                  formik.resetForm();
                  setIsCreatingNewCredential(false);
                  mapCallback(createResp.data);
                  onClose();
                }
              } else {
                console.error("Create credential failed:", createResp.data);
              }
            } catch (err) {
              console.error("Error creating credential:", err);
              toast.error("Failed to create credential");
            }

            return;
          }

          const response = await axiosInstance.post(
            deviceId != null ? END_POINTS.MAP_CRED : END_POINTS.CREDENTIAL_MGMT,
            payload
          );
          if (response.status === 200) {
            if (deviceId != null) {
              toast.success(response.data.message);
            } else {
              toast.success(response.data.message);
            }
            formik.resetForm();
            setIsCreatingNewCredential(false);
            mapCallback(response.data);
            onClose();
          } else {
            toast.error(response.data.message);

            console.error("Rescan failed:", response.data);
          }

        }

      } catch (error) {
        console.error("Error during rescan:", error);
      }
    },
    validate: (values) => {
      const errors: any = {};
      if (
        values.selectedCredential !== "create_new" &&
        !values.selectedCredential
      ) {
        errors.selectedCredential = "Please select a credential.";
      }
      // Require PEM file when creating new SSH credential with private key
      if (
        values.selectedCredential === "create_new" &&
        values.credentialType === "ssh" &&
        values.passwordType === "private_key" &&
        !ppkFile
      ) {
        errors.privateFile = "Please upload a PEM file (.pem).";
      }
      return errors;
    },
  });

  const handleCredentialTypeChange = async (event: any) => {
    const value = event.target.value;
    resetTouchedFields();
    await formik.setFieldValue("credentialType", value);
    if (value === "ssh") {
      formik.setFieldValue("apiToken", "");
      formik.setFieldValue("passwordType", "password");
    } else if (value === "api") {
      await formik.setFieldValue("user", "");
      await formik.setFieldValue("passwordType", "none");
      await formik.setFieldValue("password", "");
      await formik.setFieldValue("execPassword", "");
      await formik.setFieldValue("showExecPassword", false);
      await formik.setFieldValue("autoAssign", false);
    }
    await formik.validateForm();
  };

  const handlePasswordTypeChange = async (event: any) => {
    const value = event.target.value;
    resetTouchedFields();
    await formik.setFieldValue("passwordType", value);
    if (value === "private_key" || value === "none") {
      await formik.setFieldValue("execPassword", "");
      await formik.setFieldValue("showExecPassword", false);
      await formik.setFieldValue("autoAssign", false);
      await formik.setFieldValue("password", "");
      setShowExecEyePassword(false);
    }
    await formik.validateForm();
  };

  const handlePpkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.pem')) {
        toast.error("Please upload a PEM file (.pem)");
        if (ppkFileInputRef.current) {
          ppkFileInputRef.current.value = '';
        }
        return;
      }
      setPpkFile(file);
      setPrivateKeyFileName(file.name);
    }
  };

  const handlePpkUploadClick = () => {
    ppkFileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={() => {
      onClose();
      formik.resetForm();
      setIsCreatingNewCredential(false);
    }} PaperProps={{
      sx: {
        width: 400,
        maxWidth: 'none', // prevents MUI from restricting width
      },
    }}>
      <DialogTitle sx={{ fontWeight: "bold" }}>Map credential</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Select a credential to map the asset.
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            select
            fullWidth
            label="Select Credentials *"
            value={formik.values.selectedCredential}
            onChange={handleCredentialChange}
            error={
              formik.touched.selectedCredential &&
              Boolean(formik.errors.selectedCredential)
            }
            helperText={
              formik.touched.selectedCredential &&
                typeof formik.errors.selectedCredential === "string"
                ? formik.errors.selectedCredential
                : ""
            }
            SelectProps={{
              renderValue: (selected) => {
                if (selected === "create_new") {
                  return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AddCircleOutline sx={{ color: "blue", mr: 1 }} />
                      Create New Credential
                    </Box>
                  );
                }
                const selectedCred = assetsCredData.find((cred: any) => cred.name === selected);
                if (selectedCred) {
                  return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {selectedCred.global_cred && <Public sx={{ color: "gray", mr: 1 }} />}
                      {selectedCred.name}
                    </Box>
                  );
                }
                return selected;
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    mt: 1,
                    minHeight: "100px",
                  },
                },
              },
            }}
          >
            <MenuItem value="create_new">
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <AddCircleOutline sx={{ color: "blue", mr: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Create New Credential
                </Typography>
              </Box>
            </MenuItem>
            {assetsCredData.map((cred: any) => (
              <MenuItem key={cred.name} value={cred.name}>
                {cred.global_cred && <Public sx={{ color: "gray", mr: 1 }} />}
                {cred.name}
              </MenuItem>
            ))}
          </TextField>

          {isCreatingNewCredential && (
            <>
              <RadioGroup
                row
                value={formik.values.credentialType}
                onChange={handleCredentialTypeChange}
                sx={{ mb: 2 }}
              >
                <FormControlLabel value="ssh" control={<Radio />} label="SSH" />
                <FormControlLabel value="api" control={<Radio />} label="API" />
              </RadioGroup>

              {formik.values.credentialType === "ssh" && (
                <>
                  {/* User Input */}
                  <TextField
                    fullWidth
                    label="User *"
                    variant="outlined"
                    value={formik.values.user}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="user"
                    error={formik.touched.user && Boolean(formik.errors.user)}
                    helperText={
                      formik.touched.user &&
                        typeof formik.errors.user === "string"
                        ? formik.errors.user
                        : ""
                    }
                    sx={{ mb: 2 }}
                  />

                  {/* Password Type Radio Buttons */}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Password Type
                  </Typography>
                  <RadioGroup
                    row
                    value={formik.values.passwordType}
                    onChange={handlePasswordTypeChange}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="password"
                      control={<Radio />}
                      label="Password"
                    />
                    <FormControlLabel
                      value="private_key"
                      control={<Radio />}
                      label="Private Key"
                    />
                    {/* <FormControlLabel
                      value="none"
                      control={<Radio />}
                      label="None"
                    /> */}
                  </RadioGroup>

                  {/* Password Input */}
                  {formik.values.passwordType === "password" && (
                    <>
                      <TextField
                        fullWidth
                        label="Password *"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        name="password"
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password &&
                            typeof formik.errors.password === "string"
                            ? formik.errors.password
                            : ""
                        }
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />
                      {/* Exec Password */}
                      <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          Exec Password
                        </Typography>
                        <IOSSwitch
                          checked={formik.values.showExecPassword}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "showExecPassword",
                              e.target.checked
                            );
                            if (!e.target.checked) {
                              formik.setFieldValue("execPassword", "");
                              formik.setFieldTouched("execPassword", false, false);
                            }
                          }}
                          disabled={formik.values.autoAssign}
                        />
                      </Box>
                      {formik.values.showExecPassword && (
                        <TextField
                          fullWidth
                          label="Exec Password *"
                          type={showExecEyePassword ? "text" : "password"}
                          variant="outlined"
                          value={formik.values.execPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name="execPassword"
                          error={
                            formik.touched.execPassword &&
                            Boolean(formik.errors.execPassword)
                          }
                          helperText={
                            formik.touched.execPassword &&
                              typeof formik.errors.execPassword === "string"
                              ? formik.errors.execPassword
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={handleToggleExecPasswordVisibility}
                              >
                                {showExecEyePassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                      {/* Auto Assign */}
                      {!formik.values.showExecPassword && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-start"
                          sx={{ mb: 2 }}
                        >
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ mr: 2 }}>
                              Auto Assign
                            </Typography>
                            <IOSSwitch
                              checked={formik.values.autoAssign}
                              onChange={async (e) => {
                                await formik.setFieldValue(
                                  "autoAssign",
                                  e.target.checked
                                );
                                if (e.target.checked) {
                                  formik.setFieldValue("execPassword", "");
                                  formik.setFieldValue(
                                    "showExecPassword",
                                    false
                                  );
                                }
                                await formik.validateForm();
                              }}
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            This will auto-assign Login Password as Exec
                            Password when missing.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}

                  {formik.values.passwordType === "private_key" && (
                    <>
                      <Box
                        sx={{
                          border: "1px dashed #3a2ada",
                          padding: "10px",
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="file"
                          accept=".pem"
                          ref={ppkFileInputRef}
                          onChange={handlePpkFileChange}
                          style={{ display: "none" }}
                        />
                        <Button
                          variant="contained"
                          onClick={handlePpkUploadClick}
                          sx={{
                            backgroundColor: "#3A2AdA",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "#3225b7",
                            },
                          }}
                        >
                          Upload PEM File
                        </Button>
                        {privateKeyFileName && (
                          <Box sx={{ mt: 1, fontSize: "12px", color: "text.secondary" }}>
                            Selected: {privateKeyFileName}
                          </Box>
                        )}
                        {formik.errors.privateFile && (
                          <Box sx={{ mt: 1 }}>
                            <Typography color="error" variant="caption">
                              {String(formik.errors.privateFile)}
                            </Typography>
                          </Box>
                        )}
                        <Box>
                          <p>If a PPK file is available, it should be converted into a PEM file</p>
                        </Box>
                      </Box>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <p>Exec Password</p>
                        <IOSSwitch
                          checked={formik.values.showExecPassword}
                          onChange={async (e) => {
                            const isChecked = e.target.checked;
                            await formik.setFieldValue("showExecPassword", isChecked);
                            if (!isChecked) {
                              formik.setFieldValue("execPassword", "");
                              formik.setFieldTouched("execPassword", false, false);
                              setShowExecEyePassword(false);
                            }
                          }}
                        />
                      </div>

                      {formik.values.showExecPassword && (
                        <TextField
                          label="Exec Password"
                          type={showExecEyePassword ? "text" : "password"}
                          fullWidth
                          value={formik.values.execPassword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          name="execPassword"
                          error={
                            formik.touched.execPassword &&
                            Boolean(formik.errors.execPassword)
                          }
                          helperText={
                            formik.touched.execPassword &&
                              typeof formik.errors.execPassword === "string"
                              ? formik.errors.execPassword
                              : ""
                          }
                          InputProps={{
                            endAdornment: (
                              <IconButton onClick={handleToggleExecPasswordVisibility}>
                                {showExecEyePassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                          sx={{ mb: 2 }}
                        />
                      )}
                    </>
                  )}
                </>
              )}

              {formik.values.credentialType === "api" && (
                <TextField
                  fullWidth
                  label="API Token *"
                  variant="outlined"
                  value={formik.values.apiToken}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="apiToken"
                  error={
                    formik.touched.apiToken && Boolean(formik.errors.apiToken)
                  }
                  helperText={
                    formik.touched.apiToken &&
                      typeof formik.errors.apiToken === "string"
                      ? formik.errors.apiToken
                      : ""
                  }
                  sx={{ mb: 2 }}
                />
              )}

              {/* Credential Name Input */}
              <TextField
                fullWidth
                label="Credential Name *"
                variant="outlined"
                value={formik.values.credentialName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="credentialName"
                error={
                  formik.touched.credentialName &&
                  Boolean(formik.errors.credentialName)
                }
                helperText={
                  formik.touched.credentialName &&
                    typeof formik.errors.credentialName === "string"
                    ? formik.errors.credentialName
                    : ""
                }
                sx={{ mb: 2 }}
              />

              {/* Save Credentials Checkbox */}
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={formik.values.saveCredentials}
                  onChange={(e) =>
                    formik.setFieldValue("saveCredentials", e.target.checked)
                  }
                />
                <Typography variant="body2">Save Credentials</Typography>
              </Box>
            </>
          )}
        </form>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          paddingBottom: "24px",
          // marginTop: "12px",
        }}
      >
        <Button
          onClick={() => {
            onClose();
            formik.resetForm();
            setIsCreatingNewCredential(false);
          }}
          variant="outlined"
          color="secondary"
          sx={{ width: 130 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || formik.isSubmitting}
          sx={{ width: 130 }}
        >
          Map
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapCredentialPopup;
